import { useEffect, useMemo, useState } from 'react'

import { type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetIntegrationTeammatesQuery } from '../../../../store/integrations/integrations'
import { useLazyGetUserByIdQuery } from '../../../../store/user/user'
import { type User } from '../../../../models/User'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'

const SELECT_ASSIGNEE_OPTION_ID = '__select_assignee__'
const SELECT_ASSIGNEE_OPTION: SelectOption = { id: SELECT_ASSIGNEE_OPTION_ID, label: 'Select assignee' }

interface UseCrmAssigneeResult {
  selectedAssignee: User | null
  selectedAssigneeExternalId: string | null
  assigneeOptions: SelectOption[]
  selectedAssigneeOption: SelectOption
  handleAssigneeOptionSelected: (option: SelectOption) => void
}

// Shared assignee picker for the CRM task modals. Each CRM resolves a teammate's external
// id from its own `serviceId`; only that argument differs between modals.
export const useCrmAssignee = (serviceId: IntegrationServiceId, ownerId: string | undefined): UseCrmAssigneeResult => {
  const accountId = useAppSelector(selectAccountId)

  const { data: teammatesData } = useGetIntegrationTeammatesQuery({
    accountId,
    connector: serviceId,
  })

  const [assigneeOverride, setAssigneeOverride] = useState<User | null>(null)

  const teammatesByExternalId = useMemo(() => {
    const map = new Map<string, User>()
    for (const user of teammatesData?.data ?? []) {
      const externalId = user.integrations?.find((i) => i.serviceId === serviceId)?.externalId
      if (!externalId || map.has(externalId)) {
        continue
      }
      map.set(externalId, user)
    }
    return map
  }, [teammatesData, serviceId])

  const [fetchOwner, { data: ownerData }] = useLazyGetUserByIdQuery()
  useEffect(() => {
    if (ownerId) {
      fetchOwner({ accountId, userId: ownerId })
    }
  }, [ownerId, accountId, fetchOwner])

  const ownerExternalId = ownerData?.data?.integrations?.find((i) => i.serviceId === serviceId)?.externalId
  const defaultAssignee = ownerExternalId ? (teammatesByExternalId.get(ownerExternalId) ?? null) : null
  const selectedAssignee = assigneeOverride ?? defaultAssignee

  const assigneeOptions: SelectOption[] = useMemo(() => {
    const teammateOptions: SelectOption[] = []
    for (const [externalId, user] of teammatesByExternalId) {
      const label = getUserDisplayName(user)
      if (!label) {
        continue
      }
      teammateOptions.push({ id: externalId, label })
    }
    return [SELECT_ASSIGNEE_OPTION, ...teammateOptions]
  }, [teammatesByExternalId])

  const selectedAssigneeExternalId = useMemo<string | null>(() => {
    if (!selectedAssignee) {
      return null
    }
    return selectedAssignee.integrations?.find((i) => i.serviceId === serviceId)?.externalId ?? null
  }, [selectedAssignee, serviceId])

  const selectedAssigneeOption: SelectOption = useMemo(() => {
    if (!selectedAssignee || !selectedAssigneeExternalId) {
      return SELECT_ASSIGNEE_OPTION
    }
    return { id: selectedAssigneeExternalId, label: getUserDisplayName(selectedAssignee) }
  }, [selectedAssignee, selectedAssigneeExternalId])

  const handleAssigneeOptionSelected = (option: SelectOption) => {
    if (option.id === SELECT_ASSIGNEE_OPTION_ID) {
      setAssigneeOverride(null)
      return
    }
    const user = teammatesByExternalId.get(option.id)
    if (user) {
      setAssigneeOverride(user)
    }
  }

  return { selectedAssignee, selectedAssigneeExternalId, assigneeOptions, selectedAssigneeOption, handleAssigneeOptionSelected }
}
